import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  QueueOverview,
  QUEUE_LABELS,
  JOB_STATE_COLORS,
  QueueName,
} from "types/queue";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StorageIcon from "@mui/icons-material/Storage";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";

const useStyles = makeStyles((theme: any) => ({
  card: {
    height: "100%",
    borderRadius: "16px !important",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08) !important",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    overflow: "visible",
    position: "relative",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 30px rgba(0,0,0,0.12) !important",
    },
  },
  cardContent: {
    padding: "24px !important",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  queueIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  rgsIcon: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  },
  cmsIcon: {
    backgroundColor: "#f3e5f5",
    color: "#7b1fa2",
  },
  queueName: {
    fontWeight: 700,
    fontSize: 18,
    color: "#1d1030",
    marginBottom: 4,
  },
  queueDescription: {
    fontSize: 12,
    color: "#666",
  },
  statusChip: {
    fontWeight: 600,
    fontSize: 11,
    height: 24,
  },
  statsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
    marginBottom: 20,
  },
  statCard: {
    padding: "12px 16px",
    borderRadius: 12,
    backgroundColor: "#f8f9fa",
    border: "1px solid #eee",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  statValue: {
    fontWeight: 700,
    fontSize: 24,
    lineHeight: 1.2,
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 11,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontWeight: 500,
  },
  highlightCard: {
    gridColumn: "span 2",
    display: "flex",
    justifyContent: "space-around",
    padding: "16px",
    backgroundColor: "#fff",
    border: "2px solid #1d1030",
    borderRadius: 12,
  },
  highlightItem: {
    textAlign: "center",
  },
  highlightValue: {
    fontWeight: 800,
    fontSize: 28,
    color: "#1d1030",
  },
  highlightLabel: {
    fontSize: 12,
    color: "#666",
    fontWeight: 500,
  },
  divider: {
    width: 1,
    backgroundColor: "#e0e0e0",
  },
  actionsContainer: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    paddingTop: 16,
    borderTop: "1px solid #eee",
  },
  actionButton: {
    flex: 1,
    minWidth: 100,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
    borderRadius: "8px !important",
  },
  viewButton: {
    backgroundColor: "#1d1030 !important",
    color: "#fff !important",
    "&:hover": {
      backgroundColor: "#2d2040 !important",
    },
  },
}));

interface QueueStatsCardProps {
  queue: QueueOverview;
  onPauseResume?: (queueName: QueueName, isPaused: boolean) => void;
  onClean?: (queueName: QueueName) => void;
  showActions?: boolean;
  actionLoading?: boolean;
}

const QueueStatsCard: React.FC<QueueStatsCardProps> = ({
  queue,
  onPauseResume,
  onClean,
  showActions = false,
  actionLoading = false,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const totalPending =
    queue.stats.waiting + queue.stats.delayed + queue.stats.active;
  const isRgsQueue = queue.queueName === "rgs-queue";

  const getStatColor = (stat: string) => {
    return JOB_STATE_COLORS[stat as keyof typeof JOB_STATE_COLORS] || "#9e9e9e";
  };

  const getQueueRoute = () => {
    return isRgsQueue
      ? configData.routes.queueMonitoring.rgsQueue
      : configData.routes.queueMonitoring.cmsQueue;
  };

  const getQueueDescription = () => {
    return isRgsQueue
      ? "Credit retries, refund retries, game completions"
      : "Email sending, Slack messages, exports";
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        {/* Header */}
        <Box className={classes.cardHeader}>
          <Box>
            <Box
              className={`${classes.queueIcon} ${
                isRgsQueue ? classes.rgsIcon : classes.cmsIcon
              }`}
            >
              <StorageIcon fontSize="medium" />
            </Box>
            <Typography className={classes.queueName}>
              {QUEUE_LABELS[queue.queueName]}
            </Typography>
            <Typography className={classes.queueDescription}>
              {getQueueDescription()}
            </Typography>
          </Box>
          <Chip
            icon={queue.isPaused ? <PauseCircleIcon /> : <PlayCircleIcon />}
            label={queue.isPaused ? "Paused" : "Running"}
            size="small"
            className={classes.statusChip}
            sx={{
              backgroundColor: queue.isPaused ? "#fff3e0" : "#e8f5e9",
              color: queue.isPaused ? "#e65100" : "#2e7d32",
              "& .MuiChip-icon": {
                color: queue.isPaused ? "#e65100" : "#2e7d32",
              },
            }}
          />
        </Box>

        {/* Stats Grid */}
        <Box className={classes.statsContainer}>
          <Box className={classes.statCard}>
            <Typography
              className={classes.statValue}
              sx={{ color: getStatColor("waiting") }}
            >
              {queue.stats.waiting}
            </Typography>
            <Typography className={classes.statLabel}>Waiting</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography
              className={classes.statValue}
              sx={{ color: getStatColor("active") }}
            >
              {queue.stats.active}
            </Typography>
            <Typography className={classes.statLabel}>Active</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography
              className={classes.statValue}
              sx={{ color: getStatColor("delayed") }}
            >
              {queue.stats.delayed}
            </Typography>
            <Typography className={classes.statLabel}>Delayed</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography
              className={classes.statValue}
              sx={{ color: getStatColor("failed") }}
            >
              {queue.stats.failed}
            </Typography>
            <Typography className={classes.statLabel}>Failed</Typography>
          </Box>

          {/* Highlight Row */}
          <Box className={classes.highlightCard}>
            <Box className={classes.highlightItem}>
              <Typography className={classes.highlightValue}>
                {totalPending}
              </Typography>
              <Typography className={classes.highlightLabel}>
                Total Pending
              </Typography>
            </Box>
            <Box className={classes.divider} />
            <Box className={classes.highlightItem}>
              <Typography className={classes.highlightValue}>
                {queue.stats.completed}
              </Typography>
              <Typography className={classes.highlightLabel}>
                Completed
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Actions */}
        <Box className={classes.actionsContainer}>
          <Button
            variant="contained"
            size="small"
            className={`${classes.actionButton} ${classes.viewButton}`}
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => navigate(getQueueRoute())}
          >
            View Jobs
          </Button>
          {showActions && (
            <>
              <Tooltip
                title={
                  queue.isPaused ? "Resume processing" : "Pause processing"
                }
              >
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.actionButton}
                  startIcon={
                    queue.isPaused ? <PlayCircleIcon /> : <PauseCircleIcon />
                  }
                  onClick={() =>
                    onPauseResume?.(queue.queueName, queue.isPaused)
                  }
                  disabled={actionLoading}
                  color={queue.isPaused ? "success" : "warning"}
                >
                  {queue.isPaused ? "Resume" : "Pause"}
                </Button>
              </Tooltip>
              <Tooltip title="Clean old jobs">
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.actionButton}
                  startIcon={<CleaningServicesIcon />}
                  onClick={() => onClean?.(queue.queueName)}
                  disabled={actionLoading}
                  color="error"
                >
                  Clean
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default QueueStatsCard;
